import React, { useEffect, useMemo, useState } from "react";
import { Archive, Edit3, Plus, Search, UserRound } from "lucide-react";

const EMPTY = {
  id: 0, azienda_id: "", nome: "", cognome: "", codice_fiscale: "", data_nascita: "",
  luogo_nascita: "", indirizzo: "", email: "", telefono: "", mansione: "",
  ruolo_aziendale: "", reparto: "", data_assunzione: "", stato: "attivo", note: "",
};

async function apiJson(url, options = {}) {
  const response = await fetch(url, { credentials: "include", ...options });
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.ok === false) throw new Error(data?.error || "Operazione non riuscita");
  return data;
}

export default function DipendentiAdmin({ isSuper }) {
  const [aziende, setAziende] = useState([]);
  const [aziendaId, setAziendaId] = useState("");
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiJson("/api/admin/aziende_list.php")
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setAziende(list);
        if (list.length === 1) setAziendaId(String(list[0].id));
      })
      .catch((e) => setError(e.message));
  }, []);

  const load = async () => {
    if (isSuper && !aziendaId) { setItems([]); return; }
    setLoading(true); setError("");
    try {
      const qs = aziendaId ? `?azienda_id=${encodeURIComponent(aziendaId)}` : "";
      const data = await apiJson(`/api/admin/dipendenti_list.php${qs}`);
      setItems(data.dipendenti || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [aziendaId, isSuper]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (!showInactive && item.stato !== "attivo") return false;
      return !needle || [item.nome, item.cognome, item.codice_fiscale, item.mansione, item.ruolo_aziendale, item.reparto]
        .some((value) => String(value || "").toLowerCase().includes(needle));
    });
  }, [items, query, showInactive]);

  const openNew = () => setEditing({ ...EMPTY, azienda_id: aziendaId });
  const save = async (event) => {
    event.preventDefault(); setSaving(true); setError("");
    try {
      await apiJson("/api/admin/dipendente_save.php", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(editing),
      });
      setEditing(null); await load();
    } catch (e) { setError(errorLabel(e.message)); }
    finally { setSaving(false); }
  };

  const archive = async (item) => {
    if (!window.confirm(`Segnare ${item.nome} ${item.cognome} come cessato?`)) return;
    try {
      await apiJson("/api/admin/dipendente_archive.php", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, azienda_id: aziendaId }),
      });
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><h2 className="flex items-center gap-2 text-xl font-bold text-slate-900"><UserRound size={22} /> Dipendenti</h2>
            <p className="mt-1 text-sm text-slate-600">Anagrafica del personale separata dagli utenti che accedono al portale.</p></div>
          <button type="button" onClick={openNew} disabled={isSuper && !aziendaId}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            <Plus size={16} /> Nuovo dipendente
          </button>
        </div>

        {isSuper && <label className="mt-5 block max-w-md text-sm font-medium text-slate-700">Azienda
          <select value={aziendaId} onChange={(e) => setAziendaId(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3">
            <option value="">Seleziona azienda</option>{aziende.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
          </select></label>}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <label className="flex h-10 min-w-64 flex-1 items-center gap-2 rounded-lg border border-slate-300 px-3"><Search size={16} className="text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nome, mansione, reparto..." className="min-w-0 flex-1 outline-none" /></label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Mostra sospesi e cessati</label>
        </div>
        {error && <div className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</div>}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? <div className="p-5 text-sm text-slate-600">Caricamento dipendenti...</div> : filtered.length === 0 ?
          <div className="p-8 text-center text-sm text-slate-500">Nessun dipendente presente.</div> :
          <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 text-slate-600"><tr>
            <th className="px-4 py-3">Dipendente</th><th className="px-4 py-3">Mansione e ruolo</th><th className="px-4 py-3">Contatti</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3 text-right">Azioni</th>
          </tr></thead><tbody>{filtered.map((item) => <tr key={item.id} className="border-t border-slate-100">
            <td className="px-4 py-3"><div className="font-semibold text-slate-900">{item.cognome} {item.nome}</div><div className="text-xs text-slate-500">{item.codice_fiscale || "Codice fiscale non inserito"}</div></td>
            <td className="px-4 py-3"><div>{item.mansione || "-"}</div><div className="text-xs text-slate-500">{[item.ruolo_aziendale, item.reparto].filter(Boolean).join(" · ")}</div></td>
            <td className="px-4 py-3"><div>{item.email || "-"}</div><div className="text-xs text-slate-500">{item.telefono}</div></td>
            <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${item.stato === "attivo" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{item.stato}</span></td>
            <td className="px-4 py-3"><div className="flex justify-end gap-2"><button onClick={() => setEditing({ ...EMPTY, ...item, azienda_id: aziendaId || item.azienda_id })} className="rounded-lg border p-2" title="Modifica"><Edit3 size={15} /></button>
              {item.stato !== "cessato" && <button onClick={() => archive(item)} className="rounded-lg border p-2 text-amber-700" title="Segna come cessato"><Archive size={15} /></button>}</div></td>
          </tr>)}</tbody></table></div>}
      </div>

      {editing && <div className="fixed inset-0 z-50 overflow-y-auto bg-black/45 p-4"><div className="mx-auto my-6 max-w-3xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4"><h3 className="font-bold text-slate-900">{editing.id ? "Modifica dipendente" : "Nuovo dipendente"}</h3><button onClick={() => setEditing(null)} className="text-slate-500">Chiudi</button></div>
        <form onSubmit={save} className="p-5"><div className="grid gap-4 md:grid-cols-2">
          <Input label="Nome *" value={editing.nome} set={(v) => setEditing({ ...editing, nome: v })} required />
          <Input label="Cognome *" value={editing.cognome} set={(v) => setEditing({ ...editing, cognome: v })} required />
          <Input label="Codice fiscale" value={editing.codice_fiscale} set={(v) => setEditing({ ...editing, codice_fiscale: v.toUpperCase() })} maxLength={16} />
          <Input label="Data di nascita" type="date" value={editing.data_nascita} set={(v) => setEditing({ ...editing, data_nascita: v })} />
          <Input label="Luogo di nascita" value={editing.luogo_nascita} set={(v) => setEditing({ ...editing, luogo_nascita: v })} />
          <Input label="Indirizzo di residenza" value={editing.indirizzo} set={(v) => setEditing({ ...editing, indirizzo: v })} />
          <Input label="Email" type="email" value={editing.email} set={(v) => setEditing({ ...editing, email: v })} />
          <Input label="Telefono" value={editing.telefono} set={(v) => setEditing({ ...editing, telefono: v })} />
          <Input label="Mansione" value={editing.mansione} set={(v) => setEditing({ ...editing, mansione: v })} />
          <Input label="Ruolo aziendale" value={editing.ruolo_aziendale} set={(v) => setEditing({ ...editing, ruolo_aziendale: v })} />
          <Input label="Reparto / sede" value={editing.reparto} set={(v) => setEditing({ ...editing, reparto: v })} />
          <Input label="Data di assunzione" type="date" value={editing.data_assunzione} set={(v) => setEditing({ ...editing, data_assunzione: v })} />
          <label className="text-sm font-medium text-slate-700">Stato<select value={editing.stato} onChange={(e) => setEditing({ ...editing, stato: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3"><option value="attivo">Attivo</option><option value="sospeso">Sospeso</option><option value="cessato">Cessato</option></select></label>
          <label className="text-sm font-medium text-slate-700 md:col-span-2">Note<textarea rows={3} value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
        </div><div className="mt-5 flex justify-end gap-3"><button type="button" onClick={() => setEditing(null)} className="rounded-lg border px-4 py-2">Annulla</button><button disabled={saving} className="rounded-lg bg-indigo-700 px-5 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Salvataggio..." : "Salva dipendente"}</button></div></form>
      </div></div>}
    </section>
  );
}

function Input({ label, value, set, type = "text", ...props }) {
  return <label className="text-sm font-medium text-slate-700">{label}<input type={type} value={value || ""} onChange={(e) => set(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 outline-none focus:border-indigo-600" {...props} /></label>;
}

function errorLabel(code) {
  if (code === "CODICE_FISCALE_DUPLICATO") return "Esiste già un dipendente con questo codice fiscale.";
  if (code === "INVALID_CODICE_FISCALE") return "Il codice fiscale deve contenere 16 caratteri.";
  if (code === "NOME_COGNOME_REQUIRED") return "Nome e cognome sono obbligatori.";
  if (code === "INVALID_EMAIL") return "Indirizzo email non valido.";
  return code;
}
